<template>
	<div>
		<!-- delete dialog -->
		<bee-delete-dialog
			v-model="deleteDialog"
			moduleType="coupons"
			:ids="deleteItemId"
			@on-delete="fetchCoupons"
		>
			{{$t('are-you-sure-you-want-to')}} 
			<span class="text-uppercase error--text">{{$t('delete')}}</span> 
			{{$t('Coupons.the-coupon')}}
		</bee-delete-dialog>

		<!-- add/edit dialog -->
		<v-dialog v-model="dialog" width="600" persistent>
			<v-form ref="form" @submit.prevent="submit">
				<v-card class="rounded-medium">
					<v-card-title
						class="justify-center text-uppercase text-h6"
						:class="{ 'mb-3': !multiLangInputs, 'pb-2': multiLangInputs }"
					>
						{{ editItemId ? $t('edit') : $t('add')}} {{$t('Coupons.coupon')}}
					</v-card-title>
					<v-card-text class="pb-2">
						<!-- lang tabs -->
						<v-lang-tabs
							v-if="multiLangInputs"
							v-model="lang"
							:disable-non-selected="dialogLoading"
							background-color="bgColor"
							class="mb-4"
							@change="updateFieldsFromDraft"
						/>

						<!-- inputs -->
						<v-row dense class="mt-0">
							<!-- name -->
							<v-col sm="6" cols="12">
								<v-text-field
									v-model="name"
									:label="$t('inputs.name')"
									outlined
									dense
									class="rounded-small"
									hide-details="auto"
									:rules="rules.required"
								></v-text-field>
							</v-col>

							<!-- coupon code -->
							<v-col sm="6" cols="12">
								<v-text-field
									v-model="couponCode"
									:label="$t('inputs.coupon-code')"
									outlined
									dense
									class="rounded-small"
									hide-details="auto"
									:rules="rules.required"
								></v-text-field>
							</v-col>

							<!-- coupon products -->
							<v-col cols="12">
								<v-autocomplete
									v-model="couponProducts"
									:label="$t('inputs.coupon-products')"
									:items="products"
									item-value="id"
									item-text="name"
									chips
									small-chips
									deletable-chips
									multiple
									outlined
									dense
									class="rounded-small"
									hide-details="auto"
									:rules="rules.required"
								></v-autocomplete>
							</v-col>

							<!-- max beneficiaries number -->
							<v-col sm="4" cols="6">
								<v-text-field
									v-model.number="maxBeneficiariesNumber"
									:label="$t('inputs.max-beneficiaries-number')"
									outlined
									dense
									class="rounded-small"
									hide-details="auto"
								></v-text-field>
							</v-col>

							<!-- from date -->
							<v-col sm="4" cols="6">
								<v-menu
									v-model="fromDateMenu"
									:close-on-content-click="false"
									:nudge-right="40"
									transition="scale-transition"
									offset-y
									min-width="auto"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											v-model="fromDate"
											:label="$t('inputs.from-date')"
											prepend-inner-icon="mdi-calendar"
											outlined
											dense
											class="rounded-small"
											hide-details="auto"
											readonly
											clearable
											v-bind="attrs"
											v-on="on"
										></v-text-field>
									</template>
									<v-date-picker
										v-model="fromDate"
										@input="fromDateMenu = false"
									></v-date-picker>
								</v-menu>
							</v-col>

							<!-- to date -->
							<v-col sm="4" cols="6">
								<v-menu
									v-model="toDateMenu"
									:close-on-content-click="false"
									:nudge-right="40"
									transition="scale-transition"
									offset-y
									min-width="auto"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											v-model="toDate"
											:label="$t('inputs.to-date')"
											prepend-inner-icon="mdi-calendar"
											outlined
											dense
											class="rounded-small"
											hide-details="auto"
											readonly
											clearable
											:rules="[() => moment(toDate).isAfter(fromDate)]"
											v-bind="attrs"
											v-on="on"
										></v-text-field>
									</template>
									<v-date-picker
										v-model="toDate"
										@input="toDateMenu = false"
									></v-date-picker>
								</v-menu>
							</v-col>

							<!-- discount value -->
							<v-col cols="12" class="d-flex flex-wrap algin-center">
								<v-radio-group
									v-model="discountType"
									row
									dense
									hide-details
									class="ma-0 pt-2"
								>
									<template v-slot:label>
										<span class="text-body-1">{{$t('inputs.discount')}}</span>
									</template>
									<v-radio :label="$t('inputs.percent')" :value="0"></v-radio>
									<v-radio :label="$t('inputs.fixed-value')" :value="1"></v-radio>
								</v-radio-group>

								<v-text-field
									v-model="discount"
									:label="$t('inputs.discount')"
									outlined
									dense
									class="rounded-small"
									hide-details="auto"
									:rules="rules.required"
									:suffix="discountType === 0 ? '%' : null"
								></v-text-field>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer/>
						<v-btn
							type="submit"
							color="primary"
							:loading="dialogLoading"
							:disabled="dialogLoading"
							class="rounded-small"
						>{{$t('save')}}</v-btn>
						<v-btn
							:disabled="dialogLoading"
							class="rounded-small"
							@click="dialog = false"
						>{{$t('cancel')}}</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- filters -->
		<v-card class="rounded-medium mb-3">
			<v-card-text class="pa-2">
				<v-row class="align-center">
					<!-- add -->
					<v-col md="8" cols="12" class="text-sm-start text-center">
						<v-btn
							dark
							color="darkGreen"
							class="rounded-small"
							@click="dialog = true"
						>
							<v-icon>mdi-plus</v-icon>
							{{$t('Coupons.add-new-coupon')}}
						</v-btn>
					</v-col>
					<!-- search -->
					<v-col md="4" cols="12" class="d-flex flex-sm-row flex-column align-center text-md-start text-center">
						<v-text-field
							v-model="search"
							:label="$t('search')"
							hide-details
							outlined
							dense
							prepend-inner-icon="mdi-magnify"
							class="rounded-medium align-self-stretch me-sm-6 mb-sm-0 mb-5"
						></v-text-field>
						<v-btn
							:color="isFiltered ? null : 'primary'"
							class="rounded-small"
							@click="isFiltered = !isFiltered"
						>
							{{isFiltered ? $t('cancel') : $t('search')}}
						</v-btn>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>
		
		<!-- table -->
		<v-data-table
			:headers="customizedHeaders"
			:items="loading ? [] : filteredSets"
			:loading="loading"
			dense
			:items-per-page="15"
			class="elevation-3 rounded-medium"
		>
			<!-- actions header -->
			<template v-slot:header.actions="{ header }">
				<div class="d-flex align-center" :class="`justify-${header.align}`">
					<span class="px-0" :class="header.class">{{header.text}}</span>
					<v-btn
						small
						icon
						@click="$eventBus.$emit(
							'headers-control',
							allHeaders.filter(c => !c.hide),
							customizedHeaders,
							(newHeaders) => {
								$eStorage.l.couponsHeaders = newHeaders;
							}
						)"
					>
						<v-icon size="18">mdi-cog</v-icon>
					</v-btn>
				</div>
			</template>

			<!-- products -->
			<template v-slot:item.products="{ item }">
				{{item.products.map(c => getProductsById(c.id).name).join(', ')}}
			</template>

			<!-- actions -->
			<template v-slot:item.actions="{ item }">
				<tooltip :text="$t('tooltip.edit')">
					<v-btn
						color="blue darken-4"
						dark
						small
						class="px-1 flex-shrink-1"
						min-width="20px"
						@click="dialog = true; editItemId = item.id"
					><v-icon>mdi-pencil</v-icon></v-btn>
				</tooltip>
				
				<tooltip :text="$t('tooltip.delete')">
					<v-btn
						color="red darken-1"
						dark
						small
						class="ms-1 px-1"
						min-width="20px"
						@click="deleteDialog = true; deleteItemId = item.id"
					>
						<v-icon>mdi-delete</v-icon>
					</v-btn>
				</tooltip>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { defaultLang, langs, multiLangInputs } from '@/configs/translates.config';
import rules from '@/helpers/validation rules'
import moment from 'moment';
import langsDraft from '@/mixins/langs-draft';

export default {
	name: 'Coupons',
	mixins: [langsDraft],

	data: () => ({
		dialog: false,
		editItemId: null,
		
		deleteDialog: false,
		deleteItemId: null,

		fromDateMenu: null,
		toDateMenu: null,
		
		discountType: 0,

		// filter
		search: null,
		isFiltered: false,

		// configs
		multiLangInputs,

		// helpers
		loading: null,
		dialogLoading: null,
		lang: defaultLang,
		rules,
		moment,

		// draft for languages 
		draft: {},
		draftNames: ['name'],

		// data
		name: null,
		maxBeneficiariesNumber: null,
		couponCode: null,
		couponProducts: null,
		fromDate: null,
		toDate: null,
		discount: null,
	}),

	watch: {
		dialog(val) {
			if (val) {
				if (this.editItemId) {
					if (multiLangInputs) {
						this.fetchCoupon();
					} else {
						const item = this.getCouponBySlug(this.editItemId);
						this.name = item.name;
						this.maxBeneficiariesNumber = item.maxBeneficiariesNumber;
						this.couponProducts = item.couponProducts;
						this.couponCode = item.couponCode;
						this.fromDate = item.fromDate;
						this.toDate = item.toDate;
					}
				} else {
					this.couponCode = Date.now().toString(16);
					this.discountType = 0;
				}
			} else {
				this.$refs.form.reset();
				this.resetDraft();
				this.editItemId = null;
				this.lang = defaultLang;
			}
		}
	},

	computed: {
		...mapState({
			products: state => state.products.products,
			coupons: state => state.coupons.coupons,
		}),

		...mapGetters({
			getCouponBySlug: 'coupons/getCouponBySlug',
			getProductsById: 'products/getProductsById'
		}),

		allHeaders() {
			const headers = [
				{
					text: this.$t('headers.name'),
					value: 'name',
					align: 'center',
					class: 'primary--text',
					cellClass: 'primary--text',
					noRemove: true,
				},
				{
					text: this.$t('headers.coupon-code'),
					value: 'coupon-code',
					align: 'center',
					noRemove: true,
				},
				{
					text: this.$t('headers.max-beneficiaries-number'),
					value: 'coupon-code',
					align: 'center',
					notInDefaultView: true
				},
				{
					text: this.$t('headers.products'),
					value: 'products',
					align: 'center',
					notInDefaultView: true
				},
				{
					text: this.$t('headers.from-date'),
					value: 'from-date',
					align: 'center',
				},
				{
					text: this.$t('headers.to-date'),
					value: 'to-date',
					align: 'center',
				},
				{
					text: this.$t('headers.discount'),
					value: 'discount',
					align: 'center',
				},
				{
					text: this.$t('headers.actions'),
					value: 'actions',
					align: 'center',
					sortable: false,
					noRemove: true,
					fixed: true
				},
			]

			for (let i = 0; i < headers.length; i++) {
				headers[i].class = headers[i].class ? headers[i].class + ' px-2' : 'px-2'
				headers[i].cellClass = headers[i].cellClass ? headers[i].cellClass + ' px-2' : 'px-2'
			}

			return headers
		},

		customizedHeaders() {
			let headers = [];
			if (this.$eStorage.l.couponsHeaders.length) {
				headers = this.$eStorage.l.couponsHeaders;
			} else {
				headers = this.allHeaders.filter(c => !c.notInDefaultView);
			}
			return headers.filter(c => !c.hide);
		},

		filteredSets() {
			return this.filterByName(this.isFiltered, this.coupons, this.search);
		}
	},

	methods: {
		async submit() {
			if (this.$refs.form.validate()) {
				this.dialogLoading = true;
				
				const promises = [];
				let itemData = null;

				const submitRequest = (
					isDefaultOne,
					{ name, lang },
					id = this.editItemId
				) => {
					const actionName = id ? 'update' : 'create';
					return this.$store.dispatch(`albums/${actionName}`, {
						id: id,
						name: name,
						maxBeneficiariesNumber: this.maxBeneficiariesNumber,
						couponCode: this.couponCode,
						couponProducts: this.couponProducts,
						fromDate: this.fromDate,
						toDate: this.toDate,
						discount: this.discount,
						acceptLanguage: lang
					}).then((data) => data)
				};

				const filteredLangs = langs.filter(c => multiLangInputs || c.key === defaultLang);
				for (let index = 0; index < filteredLangs.length; index++) {
					const lang = filteredLangs[index].key;

					const name = this.draft.name[lang];

					const dataDueLang = { name, lang };

					if (name) {
						if (index === 0) {
							await submitRequest(index === 0, dataDueLang).then(data => { itemData = data });
						} else {
							promises.push(submitRequest(false, dataDueLang, itemData.id));
						}
					}
				}

				const successHandler = () => {
					const message =
						this.editItemId
							? this.$t('$-is-updated-successfully', { name: this.$t('Coupons.coupon') })
							: this.$t('$-is-created-successfully', { name: this.$t('Coupons.coupon') });
					this.$eventBus.$emit('show-snackbar', false, message);
					
					this.fetchCoupons();
					this.dialog = false;
				};
				const errorHandler = () => {
					if (itemData) {
						this.editItemId = itemData.id;
					}
				}

				Promise.all(promises).then(successHandler).catch(errorHandler).finally(() => {
					this.dialogLoading = false;
				})
			}
		},

		fetchCoupons(isFirstLoad) {
			this.loading = true;
			this.$store.dispatch('coupons/fetchAll')
				.finally(() => {
					if (!isFirstLoad) this.loading = false;
				})
		},

		fetchCoupon() {
			this.dialogLoading = true;

			const promises = [];
			langs.filter(c => multiLangInputs || c.key === defaultLang).forEach((c, index) => {
				promises.push(
					this.$store.dispatch('coupons/fetchBySlug', { id: this.editItemId, acceptLanguage: c.key })
						.then((data) => {
							if (index === 0) {
								this.maxBeneficiariesNumber = data.maxBeneficiariesNumber;
								this.couponCode = data.couponCode;
								this.couponProducts = data.couponProducts;
								this.fromDate = data.fromDate;
								this.toDate = data.toDate;
								this.discount = data.discount;
							}
							this.draft.name[c.key] = data.name;
						})
				);
			});

			Promise.all(promises).then(() => {
				this.name = this.draft.name[this.lang]
			}).finally(() => {
				this.dialogLoading = false;
			});
		}
	},

	created() {
		this.loading = true;
		Promise.all([
			this.fetchCoupons(),
			this.$store.dispatch('products/fetchAll', {}),
		]).finally(() => {
			this.loading = false;
		})
	}
}
</script>

<style>

</style>